import React, { useState, useEffect, useContext, useRef } from "react";
import { Plus, Upload } from "lucide-react";
import { Button } from "../../../shadcn/components/ui/button";
import Drawer from "../../../Components/Common/DrawerLayout/Drawer";
import { AuthContext } from "../../../context/AuthContext";
import { CREATE_CLUB_SA, EDIT_CLUB } from "../SuperAdminService";
import { createClubLogoSaver } from "./clubLogoUpload";
import { uploadClubGdprDocument } from "./clubGdprUpload";
import ClubAdminsSection from "./ClubAdminsSection";
import Form from "../../../Components/Form/Form";
import { formValidator, isEmpty } from "../../../Components/Form/validators";
import TextFieldRenderer from "../../../Components/Form/TextFieldRenderer";
import SelectRenderer from "../../../Components/Form/SelectRenderer";
import {
  brandColorsOptions,
  countries,
  imageMimeTypesDropzone,
  sports,
  USER_ROLES,
} from "../../../utility/Enum";
import { useTranslation } from "../../../context/Internationalization";
import { axiosGet, axiosAuthenticatedPost } from "../../../api";
import useGraphql from "../../../Hooks/useGraphql";
import useLog from "../../../Hooks/useLog";
import { getStorage } from "../../../auth/utils";
import MultipleFilesUpload from "../../../Components/Form/MultipleFilesUpload";
import FileActions from "../../../Components/Common/Files/FileActions";
import {
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../../shadcn/components/ui/sheet";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../shadcn/components/ui/card";
import { cn } from "../../../shadcn/lib/utils";
import { toast } from "sonner";

const isImpersonating = Boolean(getStorage().getItem("impersonate"));

const schema = (t, roles) => [
  {
    label: t("club_name"),
    name: "name",
    validators: [
      {
        validator: isEmpty,
        text: (label) => `${label} ${t("cannot_be_empty")}`,
      },
    ],
    renderer: TextFieldRenderer,
    row: 1,
  },
  {
    label: t("abbreviation"),
    name: "abbreviation",
    maxLength: 3,
    renderer: TextFieldRenderer,
    row: 2,
  },
  {
    label: t("founded"),
    name: "founded",
    renderer: TextFieldRenderer,
    row: 3,
  },
  {
    label: t("country"),
    name: "country",
    validators: [
      {
        validator: isEmpty,
        text: (label) => `${label} ${t("cannot_be_empty")}`,
      },
    ],
    options:
      countries?.map((country) => ({
        value: country.label,
        text: country.label,
      })) || [],
    renderer: SelectRenderer,
    row: 4,
  },
  {
    label: t("sport"),
    name: "sport",
    options: sports(t),
    validators: [
      {
        validator: isEmpty,
        text: (label) => `${label} ${t("cannot_be_empty")}`,
      },
    ],
    row: 5,
    renderer: SelectRenderer,
  },
  {
    label: t("brand_color"),
    name: "brandColor",
    validators: [
      {
        validator: isEmpty,
        text: (label) => `${label} ${t("cannot_be_empty")}`,
      },
    ],
    renderer: SelectRenderer,
    options: brandColorsOptions(t) || [],
    colorPicker: true,
    row: 6,
  },
  roles.includes(USER_ROLES.SUPER_ADMIN) ||
  roles.includes(USER_ROLES.CLUB_ADMIN)
    ? {
        label: "",
        name: "clubLogo",
        renderer: MultipleFilesUpload,
        singleFileUpload: true,
        acceptedFileTypes: imageMimeTypesDropzone,
        row: 9,
      }
    : {},
  // {
  //   label: 'Location',
  //   name: 'location',
  //   disabled: selectedClub,
  //   renderer: LocationRenderer,
  //   row: 10
  // },
];

const initialState = {
  name: "",
  abbreviation: "",
  founded: "",
  country: "Greece",
  sport: "",
  brandColor: "#1B2028",
  clubLogo: null,
  location: undefined,
  gdpr: "",
};

const populateData = (club) => {
  const _initialState = { ...initialState };
  if (club) {
    _initialState.name = club.name;
    _initialState.abbreviation = club.abbreviation || "";
    _initialState.founded = club.founded || "";
    _initialState.country = club.country || "";
    _initialState.sport = club.sport || "";
    _initialState.clubLogo = club.logo ? [club.logo] : null;
    _initialState.location = {
      address: club.location?.address,
      lat: club.location?.coordinates[1],
      lng: club.location?.coordinates[0],
    };
    _initialState.gdpr = club.gdpr || "";
  }

  return _initialState;
};

const ClubsDrawer = ({ openDrawer, onCloseDrawer, onExited, selectedClub, getClubs, title, showClubAdmins = false }) => {
  const { authState } = useContext(AuthContext);
  const [values, setValues] = useState(populateData(selectedClub));
  const [pendingGdprFile, setPendingGdprFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const saveInProgress = useRef(false);
  const createdClubId = useRef(null);
  const formRevision = useRef(0);
  const savedFormRevision = useRef(null);
  const saveClubLogo = useRef(null);
  const themeEdited = useRef(false);
  const gdprInputRef = useRef(null);
  const { t } = useTranslation();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();
  const roles = authState.user.roles;
  const _schema = schema(t, roles);
  const clubDetailsSchema = _schema.filter(
    (field) =>
      field?.name && !["brandColor", "clubLogo"].includes(field.name)
  );
  const clubBrandingSchema = _schema.filter((field) =>
    ["brandColor", "clubLogo"].includes(field?.name)
  );

  useEffect(() => {
    let active = true;
    createdClubId.current = null;
    formRevision.current = 0;
    savedFormRevision.current = null;
    saveClubLogo.current = createClubLogoSaver(selectedClub?.logo);
    themeEdited.current = false;
    setPendingGdprFile(null);
    if (gdprInputRef.current) gdprInputRef.current.value = "";
    if (!openDrawer) return;
    if (selectedClub) {
      setValues(populateData(selectedClub));
      getThemeColor(selectedClub._id, () => active);
    } else {
      setValues(initialState);
    }
    return () => { active = false; };
  }, [selectedClub?._id, openDrawer]);

  const getThemeColor = (clubId, isCurrent) => {
    axiosGet(`/theme/${clubId}`)
      .then((response) => {
        if (!isCurrent() || themeEdited.current) return;
        const { primaryColor } = response.data;
        setValues((prevState) => ({ ...prevState, brandColor: primaryColor }));
      })
      .catch((error) => {
        if (!isCurrent()) return;
        errorLog(error, "ClubsDrawer", "getThemeColor", "theme");
        toast.error(error.response?.data?.error || error.message);
      });
  };

  const onClose = () => {
    if (saveInProgress.current) return;
    setPendingGdprFile(null);
    onCloseDrawer();
  };

  const handleSubmit = async () => {
    if (saveInProgress.current) return;
    const errors = formValidator({ schema: _schema, values });
    if (Object.keys(errors).length > 0) {
      setValues((prevState) => ({ ...prevState, errors }));
    } else {
      saveInProgress.current = true;
      setSaving(true);
      const existingClubId = selectedClub?._id || createdClubId.current;
      let query = CREATE_CLUB_SA;
      let variables = {
        name: values.name,
        abbreviation: values.abbreviation,
        founded: +values.founded,
        country: values.country,
        sport: values.sport,
        timezone: "Europe/Athens",
        // location: {
        //   type: "Point",
        //   address: values.location.address,
        //   coordinates: [values.location.lng, values.location.lat],
        // }
      };

      if (existingClubId) {
        query = EDIT_CLUB;
        variables = roles.includes(USER_ROLES.SUPER_ADMIN)
          ? {
              clubId: existingClubId,
              name: values.name,
              abbreviation: values.abbreviation || "",
              founded: +values.founded || 0,
              country: values.country || "",
              sport: values.sport,
              timezone: "Europe/Athens",
            }
          : {
              clubId: existingClubId,
              name: values.name,
              abbreviation: values.abbreviation || "",
              founded: +values.founded || 0,
              country: values.country || "",
              sport: values.sport,
              timezone: "Europe/Athens",
            };
      }

      try {
        // An unchanged retry after refresh failure must not repeat completed writes.
        if (savedFormRevision.current !== formRevision.current) {
          const response = await axiosGraphQL({ query, variables });
          const clubId = existingClubId || response.data.data.createClubSA._id;
          if (!selectedClub) createdClubId.current = clubId;
          // An existing club's unloaded/default theme is not a user edit.
          if (!selectedClub || themeEdited.current) {
            await axiosAuthenticatedPost(`/theme/${clubId}`, { primaryColor: values.brandColor });
          }
          await saveClubLogo.current({
            logo: values.clubLogo?.[0],
            clubId,
            graphql: axiosGraphQL,
          });

          // Publish the selected document only after Save and the preceding
          // settings writes succeed. Selection itself never creates an upload.
          if (pendingGdprFile) {
            const document = await uploadClubGdprDocument({
              file: pendingGdprFile,
              clubId,
              graphql: axiosGraphQL,
              onCleanupError: (error) => errorLog(error, "ClubsDrawer", "handleSubmit", "CANCEL_CLUB_GDPR_UPLOAD"),
            });
            setValues((previous) => ({ ...previous, gdpr: document.url }));
            // A later refresh failure must not republish the same selection.
            setPendingGdprFile(null);
          }
          savedFormRevision.current = formRevision.current;
        }

        await getClubs();
        toast.success(t(selectedClub ? "academyUpdatedSuccessfully" : "academyCreatedSuccessfully"));
        onCloseDrawer();
      } catch (error) {
        errorLog(error, "ClubsDrawer", "handleSubmit", query);
        toast.error(t(error.response?.data?.error || error.message || "gdpr_document_upload_failed"));
      } finally {
        saveInProgress.current = false;
        setSaving(false);
      }
    }
  };

  const handleGdprFileChange = (event) => {
    if (saveInProgress.current) return;
    const file = event.target.files?.[0];
    if (file) {
      formRevision.current += 1;
      setPendingGdprFile(file);
    }
    event.target.value = "";
  };

  const onChange = ({ target: { name, value } }) => {
    if (saveInProgress.current) return;
    if (name === "brandColor") themeEdited.current = true;
    formRevision.current += 1;
    setValues((prevState) => ({ ...prevState, [name]: value }));
  };

  return (
    <Drawer
      open={openDrawer}
      onClose={onClose}
      onExited={onExited}
      size="wide"
      aria-labelledby="clubs-drawer-title"
      closeLabel={t("close")}
      closeDisabled={saving}
    >
      <div className={cn("flex min-h-0 w-full flex-1 flex-col", isImpersonating ? "pt-6" : undefined)} aria-busy={saving}>
        <SheetHeader>
          <SheetTitle id="clubs-drawer-title">{title || (selectedClub ? t("edit_club") : t("create_a_club"))}</SheetTitle>
          {(t(
            selectedClub
              ? "edit_club_description"
              : "create_club_description"
          )) ? (
            <SheetDescription>{t(
            selectedClub
              ? "edit_club_description"
              : "create_club_description"
          )}</SheetDescription>
          ) : null}
        </SheetHeader>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto px-6 pb-6" data-testid="clubs-drawer-body">
          <div className="flex flex-col gap-4">
            <Card size="sm" role="region" aria-labelledby="club-details-section-title">
              <CardHeader>
                <CardTitle role="heading" aria-level={3} id="club-details-section-title">{t("club_details_title")}</CardTitle>
                <CardDescription>{t("club_details_description")}</CardDescription>
              </CardHeader>
              <CardContent>
              <Form
                schema={clubDetailsSchema.map((field) => ({ ...field, disabled: saving || field.disabled }))}
                values={values}
                onChange={onChange}
                errors={values.errors}
              />
            </CardContent>
            </Card>
            <Card size="sm" role="region" aria-labelledby="club-branding-section-title">
              <CardHeader>
                <CardTitle role="heading" aria-level={3} id="club-branding-section-title">{t("club_branding_title")}</CardTitle>
                <CardDescription>{t("club_branding_description")}</CardDescription>
              </CardHeader>
              <CardContent>
              <Form
                schema={clubBrandingSchema.map((field) => ({ ...field, disabled: saving || field.disabled }))}
                values={values}
                onChange={onChange}
                errors={values.errors}
              />
            </CardContent>
            </Card>
            {selectedClub ? (
              <Card size="sm" role="region" aria-labelledby="gdpr-document-section-title">
                <CardHeader>
                  <CardTitle role="heading" aria-level={3} id="gdpr-document-section-title">{t("gdpr_document")}</CardTitle>
                  <CardDescription>{t("gdpr_document_description")}</CardDescription>
                </CardHeader>
                <CardContent>
                <div className="flex flex-col items-start gap-2">
                  {values.gdpr ? (
                    <FileActions file={values.gdpr} />
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      {t("no_gdpr_document")}
                    </span>
                  )}
                  {pendingGdprFile && (
                    <div role="status" className="max-w-full [overflow-wrap:anywhere]">
                      <div>{pendingGdprFile.name}</div>
                      <span className="text-sm text-muted-foreground">
                        {t("gdpr_document_save_pending")}
                      </span>
                    </div>
                  )}
                  <input
                    ref={gdprInputRef}
                    type="file"
                    accept="application/pdf,.pdf,.doc,.docx,.txt"
                    aria-label={t("gdpr_document")}
                    disabled={saving}
                    hidden
                    onChange={handleGdprFileChange}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="h-auto min-h-9 whitespace-normal py-2"
                    onClick={() => gdprInputRef.current?.click()}
                    disabled={saving}
                  >
                    <Upload aria-hidden="true" />
                    {values.gdpr
                      ? t("replace_gdpr_document")
                      : t("upload_gdpr_document")}
                  </Button>
                </div>
              </CardContent>
              </Card>
            ) : null}
            {openDrawer && showClubAdmins && selectedClub?._id && roles.includes(USER_ROLES.SUPER_ADMIN) ? (
              <ClubAdminsSection key={selectedClub._id} adminIds={selectedClub.clubAdminUsers} />
            ) : null}
          </div>
        </div>
        <SheetFooter data-testid="clubs-drawer-footer">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button type="button" variant="outline" className="w-full sm:flex-1" onClick={onClose} disabled={saving}>
              {t("cancel")}
            </Button>
            <Button type="button" className="w-full sm:flex-1" onClick={handleSubmit} disabled={saving}>
              {selectedClub ? null : <Plus aria-hidden="true" />}
              {selectedClub ? t("save") : t("create")}
            </Button>
          </div>
        </SheetFooter>
      </div>
    </Drawer>
  );
};

export default ClubsDrawer;
